/* Unable to type variadic arguments, so we have to type out each variant & combine. Below we support upto 5 arguments */

/* Valid argument type for memoize function */
type ArgType = string | number | boolean | symbol | object;

/* Function variants with each arg typed */
type Func0<Return> = () => Return;
type Func1<Return, Arg1 extends ArgType> = (arg1: Arg1) => Return;
type Func2<Return, Arg1 extends ArgType, Arg2 extends ArgType> = (arg1: Arg1, arg2: Arg2) => Return;
type Func3<Return, Arg1 extends ArgType, Arg2 extends ArgType, Arg3 extends ArgType> = (
    arg1: Arg1,
    arg2: Arg2,
    arg3: Arg3,
) => Return;
type Func4<
    Return,
    Arg1 extends ArgType,
    Arg2 extends ArgType,
    Arg3 extends ArgType,
    Arg4 extends ArgType,
> = (arg1: Arg1, arg2: Arg2, arg3: Arg3, arg4: Arg4) => Return;
type Func5<Return, Arg1, Arg2, Arg3, Arg4, Arg5> = (
    arg1: Arg1,
    arg2: Arg2,
    arg3: Arg3,
    arg4: Arg4,
    arg5: Arg5,
) => Return;

/* Dynamically choose between Func1, Func2, Func3, Func4, Func5 based on the arguments used */
type FuncType<
    Return,
    Arg1 = undefined,
    Arg2 = undefined,
    Arg3 = undefined,
    Arg4 = undefined,
    Arg5 = undefined,
> = Arg1 extends ArgType
    ? Arg2 extends ArgType
        ? Arg3 extends ArgType
            ? Arg4 extends ArgType
                ? Arg5 extends ArgType
                    ? Func5<Return, Arg1, Arg2, Arg3, Arg4, Arg5>
                    : Func4<Return, Arg1, Arg2, Arg3, Arg4>
                : Func3<Return, Arg1, Arg2, Arg3>
            : Func2<Return, Arg1, Arg2>
        : Func1<Return, Arg1>
    : Func0<Return>;

/* Default argument serializer */
const _createKeyFromArray = <A extends unknown[]>(...argList: A) => {
    const stringList = argList.map(item => {
        if (typeof item === 'object') {
            return JSON.stringify(item);
        } else if (Array.isArray(item)) {
            return (item as unknown[]).map((arrayItem: unknown) => String(arrayItem));
        } else {
            return `_${String(item)}_`;
        }
    });

    return `_${stringList.join('_')}_`;
};

/**
 * Memoize
 *
 * @desc Creates a function that memoizes the result of fn.
 * The arguments of the fn are used to create a cache key.
 *
 * @param {Function} fn The function to have its output memoized.
 * @param {Function=} argsSerializer Function that will convert #fn args to a string lookup key
 * @param {Array=} Array of arg,return values to pre-insert into cached values
 * @returns {Function} Memoized fn version
 */
export function memoize<
    Return,
    Arg1 = undefined,
    Arg2 = undefined,
    Arg3 = undefined,
    Arg4 = undefined,
    Arg5 = undefined,
>(
    fn: FuncType<Return, Arg1, Arg2, Arg3, Arg4, Arg5>,
    argsSerializer: FuncType<
        string,
        Arg1,
        Arg2,
        Arg3,
        Arg4,
        Arg5
    > = _createKeyFromArray as FuncType<string, Arg1, Arg2, Arg3, Arg4, Arg5>,
    presetValues: [unknown[], Return][] = [],
): FuncType<Return, Arg1, Arg2, Arg3, Arg4, Arg5> {
    const cache = new Map<string, Return>();

    /* Loop through & set all preset values into cache first */
    for (const [argsValue, returnValue] of presetValues) {
        const [keyArg1, keyArg2, keyArg3, keyArg4, keyArg5] = argsValue;
        const serializedKey = argsSerializer(
            keyArg1 as Arg1,
            keyArg2 as Arg2,
            keyArg3 as Arg3,
            keyArg4 as Arg4,
            keyArg5 as Arg5,
        );
        cache.set(serializedKey, returnValue);
    }

    const memoFn = (arg1: Arg1, arg2: Arg2, arg3: Arg3, arg4: Arg4, arg5: Arg5): Return => {
        const cacheKey = argsSerializer(arg1, arg2, arg3, arg4, arg5);

        const cachedResult = cache.get(cacheKey);

        if (cachedResult !== undefined) {
            return cachedResult;
        }

        const result = fn(arg1, arg2, arg3, arg4, arg5);
        cache.set(cacheKey, result);
        return result;
    };

    return memoFn as FuncType<Return, Arg1, Arg2, Arg3, Arg4, Arg5>;
}

/**
 * Memoize
 *
 * @desc Creates a async function that memoizes the result of fn.
 * The arguments of the fn are used to create a cache key.
 *
 * @param {Function} fn The function to have its output memoized.
 * @param {Function=} argsSerializer Function that will convert #fn args to a string lookup key
 * @param {Array=} Array of arg,return values to pre-insert into cached values
 * @returns {Function} Memoized fn version
 */
export function memoizeAsync<
    Return,
    Arg1 = undefined,
    Arg2 = undefined,
    Arg3 = undefined,
    Arg4 = undefined,
    Arg5 = undefined,
>(
    fn: FuncType<Promise<Return>, Arg1, Arg2, Arg3, Arg4, Arg5>,
    argsSerializer: FuncType<
        string,
        Arg1,
        Arg2,
        Arg3,
        Arg4,
        Arg5
    > = _createKeyFromArray as FuncType<string, Arg1, Arg2, Arg3, Arg4, Arg5>,
    presetValues: [unknown[], Return][] = [],
): FuncType<Promise<Return>, Arg1, Arg2, Arg3, Arg4, Arg5> {
    const cache = new Map<string, Return>();

    /* Loop through & set all preset values into cache first */
    for (const [argsValue, returnValue] of presetValues) {
        const [keyArg1, keyArg2, keyArg3, keyArg4, keyArg5] = argsValue;
        const serializedKey = argsSerializer(
            keyArg1 as Arg1,
            keyArg2 as Arg2,
            keyArg3 as Arg3,
            keyArg4 as Arg4,
            keyArg5 as Arg5,
        );
        cache.set(serializedKey, returnValue);
    }

    const memoAsyncFn = async (
        arg1: Arg1,
        arg2: Arg2,
        arg3: Arg3,
        arg4: Arg4,
        arg5: Arg5,
    ): Promise<Return> => {
        const cacheKey = argsSerializer(arg1, arg2, arg3, arg4, arg5);

        const cachedResult = cache.get(cacheKey);

        if (cachedResult !== undefined) {
            return cachedResult;
        }

        const result = await fn(arg1, arg2, arg3, arg4, arg5);
        cache.set(cacheKey, result);
        return result;
    };

    return memoAsyncFn as FuncType<Promise<Return>, Arg1, Arg2, Arg3, Arg4, Arg5>;
}
